import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronRight,
  GraduationCap,
  BookOpen,
  AlertTriangle,
  Unlink,
  Loader2,
} from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import StatCard from "@/components/shared/StatCard";
import SectionCard from "@/components/shared/SectionCard";
import EmptyState from "@/components/shared/EmptyState";
import { TableSkeleton } from "@/components/shared/Skeletons";
import { SearchInput } from "@/components/shared/SearchInput";
import { TablePager } from "@/components/shared/TablePager";
import { IconAction } from "@/components/shared/IconAction";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import apiClient from "@/lib/api-client";
import type { ProgrammeOption } from "@/hooks/useReferenceData";

export const Route = createFileRoute("/admin/institutions/")({
  component: AdminInstitutionsPage,
});

interface InstitutionSummary {
  id: number;
  name: string;
  abbreviation: string;
  location: string;
  type: string;
  source: "TCU" | "NACTVET" | string;
  external_id: number | null;
  status: string;
  programme_count: number;
}

interface InstitutionPage {
  items: InstitutionSummary[];
  total: number;
  page: number;
  page_size: number;
  overview: Record<string, number>;
}

function useDebounced<T>(value: T, ms = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

function AdminInstitutionsPage() {
  const [search, setSearch] = useState("");
  const [source, setSource] = useState("all");
  const [courses, setCourses] = useState("all");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [expanded, setExpanded] = useState<number | null>(null);
  const debouncedSearch = useDebounced(search);

  // Any filter change starts again from the first page.
  useEffect(() => {
    setPage(0);
    setExpanded(null);
  }, [debouncedSearch, source, courses, pageSize]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["admin-tcu-institutions", debouncedSearch, source, courses, page, pageSize],
    queryFn: async () => {
      const res = await apiClient.get("/tcu/admin/institutions", {
        params: {
          search: debouncedSearch || undefined,
          source: source === "all" ? undefined : source,
          courses: courses === "all" ? undefined : courses,
          page: page + 1,
          page_size: pageSize,
        },
      });
      return res.data.data as InstitutionPage;
    },
    placeholderData: keepPreviousData,
  });

  const overview = data?.overview ?? {};
  const items = data?.items ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Universities & Colleges"
        subtitle="Everything the TCU and NACTVET syncs have stored, with the courses students can pick for each institution."
        breadcrumbs={[
          { label: "Dashboard", path: "/admin/dashboard" },
          { label: "Universities & Colleges" },
        ]}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4 2xl:grid-cols-4">
        <StatCard
          label={`Institutions (TCU ${overview.tcu ?? 0} · NACTVET ${overview.nactvet ?? 0})`}
          value={overview.institutions ?? 0}
          icon={<GraduationCap />}
        />
        <StatCard label="Courses" value={overview.programmes ?? 0} icon={<BookOpen />} />
        <StatCard
          label="Institutions without courses"
          value={overview.without_courses ?? 0}
          icon={<AlertTriangle />}
        />
        <StatCard
          label="Courses with no institution"
          value={overview.orphaned ?? 0}
          icon={<Unlink />}
        />
      </div>

      <SectionCard
        title={data ? `${data.total.toLocaleString()} institutions` : "Institutions"}
        actions={
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <div className="flex w-full items-center gap-2 sm:w-auto">
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search name, abbreviation, location..."
                aria-label="Search institutions"
                className="flex-1 sm:w-64 sm:flex-none"
              />
              <span className="flex size-4 shrink-0 items-center justify-center" aria-live="polite">
                {isFetching && (
                  <Loader2 className="size-3.5 animate-spin text-muted-foreground" aria-label="Loading" />
                )}
              </span>
            </div>
            <div className="flex gap-2">
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger className="h-8 flex-1 sm:w-[170px]" aria-label="Source">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sources</SelectItem>
                  <SelectItem value="TCU">TCU (universities)</SelectItem>
                  <SelectItem value="NACTVET">NACTVET (colleges)</SelectItem>
                </SelectContent>
              </Select>
              <Select value={courses} onValueChange={setCourses}>
                <SelectTrigger className="h-8 flex-1 sm:w-[160px]" aria-label="Courses">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any courses</SelectItem>
                  <SelectItem value="with">With courses</SelectItem>
                  <SelectItem value="without">Without courses</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        }
        noPadding
      >
        {isLoading ? (
          <div className="p-4">
            <TableSkeleton rows={8} columns={5} />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            bare
            icon={<GraduationCap />}
            title="No institutions found"
            description={
              overview.institutions
                ? "Nothing matches these filters. Try a different search or source."
                : "Nothing has been synced yet. Run the TCU and NACTVET syncs from Settings."
            }
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-12" />
                  <TableHead>Institution</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="pr-5 text-right">Courses</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((inst) => (
                  <InstitutionRow
                    key={inst.id}
                    inst={inst}
                    open={expanded === inst.id}
                    onToggle={() => setExpanded((cur) => (cur === inst.id ? null : inst.id))}
                  />
                ))}
              </TableBody>
            </Table>
            <TablePager
              page={page}
              pageSize={pageSize}
              total={data?.total ?? 0}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              pageSizeOptions={[25, 50, 100, 200]}
            />
          </>
        )}
      </SectionCard>
    </div>
  );
}

function InstitutionRow({
  inst,
  open,
  onToggle,
}: {
  inst: InstitutionSummary;
  open: boolean;
  onToggle: () => void;
}) {
  const noCourses = inst.programme_count === 0;
  const isTCU = inst.source === "TCU";

  return (
    <>
      <TableRow
        onClick={onToggle}
        className={cn("cursor-pointer", open && "border-b-0 bg-muted/40", noCourses && "bg-warning/5")}
      >
        <TableCell className="py-1.5">
          <IconAction
            label={open ? "Hide courses" : "Show courses"}
            aria-expanded={open}
            icon={open ? <ChevronDown /> : <ChevronRight />}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          />
        </TableCell>
        <TableCell className="min-w-56 py-1.5">
          <p className="font-medium text-foreground">{inst.name}</p>
          <p className="text-caption text-muted-foreground">
            {[
              inst.abbreviation,
              inst.external_id ? `ID ${inst.external_id}` : null,
              inst.status !== "active" ? inst.status : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </TableCell>
        <TableCell>
          <Badge variant={isTCU ? "secondary" : "outline"} className="font-mono">
            {inst.source}
          </Badge>
        </TableCell>
        <TableCell className="text-body">{inst.location || "—"}</TableCell>
        <TableCell className="text-body">{inst.type || "—"}</TableCell>
        <TableCell className="pr-5 text-right">
          {noCourses ? (
            <Badge variant="warning" dot>
              No courses
            </Badge>
          ) : (
            <span className="font-mono text-foreground tabular-nums">{inst.programme_count}</span>
          )}
        </TableCell>
      </TableRow>
      {open && (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={6} className="bg-background/40 p-0">
            <CourseList institutionId={inst.id} expected={inst.programme_count} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function CourseList({ institutionId, expected }: { institutionId: number; expected: number }) {
  const [filter, setFilter] = useState("");
  const {
    data: programmes = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["admin-tcu-programmes", institutionId],
    queryFn: async () => {
      const res = await apiClient.get(`/tcu/universities/${institutionId}/programmes`);
      return res.data.data as ProgrammeOption[];
    },
    enabled: expected > 0,
  });

  if (expected === 0) {
    return (
      <p className="py-4 pr-5 pl-4 text-sm text-muted-foreground md:pl-14">
        No courses are stored for this institution, so students who pick it will have to type their
        course.
      </p>
    );
  }
  if (isLoading) {
    return (
      <div className="py-4 pl-4 md:pl-14">
        <Loader2 className="size-4 animate-spin text-muted-foreground" aria-label="Loading courses" />
      </div>
    );
  }
  if (isError) {
    return (
      <p className="py-4 pr-5 pl-4 text-sm text-destructive md:pl-14">
        Couldn't load the courses for this institution.
      </p>
    );
  }

  const needle = filter.trim().toLowerCase();
  const shown = needle
    ? programmes.filter(
        (p) =>
          p.name.toLowerCase().includes(needle) ||
          (p.award_level || "").toLowerCase().includes(needle),
      )
    : programmes;

  return (
    <div className="py-4 pr-4 pl-4 md:pr-5 md:pl-14">
      {programmes.length > 8 && (
        <Input
          placeholder={`Filter ${programmes.length} courses...`}
          aria-label="Filter courses"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="mb-3 h-8 w-full sm:w-72"
        />
      )}
      <div className="overflow-hidden rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Course</TableHead>
              <TableHead>Award level</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Study mode</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((p) => (
              <TableRow key={p.id}>
                <TableCell className="py-2 text-[13px] text-foreground">{p.name}</TableCell>
                <TableCell className="py-2 text-[13px] text-body">{p.award_level || "—"}</TableCell>
                <TableCell className="py-2 text-[13px] text-body">{p.duration || "—"}</TableCell>
                <TableCell className="py-2 text-[13px] text-body">{p.study_mode || "—"}</TableCell>
              </TableRow>
            ))}
            {shown.length === 0 && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={4} className="py-3 text-[13px] text-muted-foreground">
                  No courses match "{filter}".
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
